import { supabaseClient } from "@config/supabaseClient";
import type {
	SignInData,
	SignUpData,
	OAuthProvider,
	AuthError,
} from "../schemas";
import type {
	User,
	Session,
	AuthError as SupabaseAuthError,
} from "@supabase/supabase-js";

// =============================================================================
// TYPE ADAPTERS (Supabase -> Our schemas)
// =============================================================================

const adaptAuthError = (error: SupabaseAuthError | null): AuthError | null => {
	if (!error) return null;
	return {
		message: error.message,
		status: error.status,
		code: error.code,
	};
};

// =============================================================================
// AUTHENTICATION
// =============================================================================

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (
	data: SignInData,
	captchaToken?: string,
): Promise<{
	user: User | null;
	session: Session | null;
	error: AuthError | null;
}> => {
	const { data: authData, error } =
		await supabaseClient.auth.signInWithPassword({
			email: data.email,
			password: data.password,
			options: {
				captchaToken,
			},
		});

	return {
		user: authData.user,
		session: authData.session,
		error: adaptAuthError(error),
	};
};

/**
 * Sign up with email and password
 */
export const signUpWithEmail = async (
	data: SignUpData,
	captchaToken?: string,
): Promise<{
	user: User | null;
	session: Session | null;
	error: AuthError | null;
}> => {
	const { data: authData, error } = await supabaseClient.auth.signUp({
		email: data.email,
		password: data.password,
		options: {
			captchaToken,
			data: {
				firstName: data.firstName,
				lastName: data.lastName,
				full_name: `${data.firstName} ${data.lastName}`,
			},
		},
	});

	return {
		user: authData.user,
		session: authData.session,
		error: adaptAuthError(error),
	};
};

/**
 * Sign in with OAuth provider
 */
export const signInWithOAuth = async (
	provider: OAuthProvider,
): Promise<{ error: AuthError | null }> => {
	const { error } = await supabaseClient.auth.signInWithOAuth({
		provider,
		options: {
			redirectTo: `${window.location.origin}/`,
		},
	});

	return {
		error: adaptAuthError(error),
	};
};

/**
 * Sign out current user
 */
export const signOut = async (): Promise<{ error: AuthError | null }> => {
	const { error } = await supabaseClient.auth.signOut();
	return {
		error: adaptAuthError(error),
	};
};

// =============================================================================
// PASSWORD MANAGEMENT
// =============================================================================

/**
 * Send password reset email
 */
export const sendPasswordResetEmail = async (
	email: string,
): Promise<{ error: AuthError | null }> => {
	const { error } = await supabaseClient.auth.resetPasswordForEmail(email, {
		redirectTo: `${window.location.origin}/update-password`,
	});

	return {
		error: adaptAuthError(error),
	};
};

/**
 * Update password (requires active session)
 */
export const updatePassword = async (
	newPassword: string,
): Promise<{ user: User | null; error: AuthError | null }> => {
	const { data, error } = await supabaseClient.auth.updateUser({
		password: newPassword,
	});

	return {
		user: data.user,
		error: adaptAuthError(error),
	};
};

// =============================================================================
// SESSION MANAGEMENT
// =============================================================================

/**
 * Get current session
 */
export const getSession = async (): Promise<{
	session: Session | null;
	error: AuthError | null;
}> => {
	const { data, error } = await supabaseClient.auth.getSession();

	return {
		session: data.session,
		error: adaptAuthError(error),
	};
};

/**
 * Refresh current session
 */
export const refreshSession = async (): Promise<{
	session: Session | null;
	error: AuthError | null;
}> => {
	const { data, error } = await supabaseClient.auth.refreshSession();

	return {
		session: data.session,
		error: adaptAuthError(error),
	};
};

/**
 * Get current user
 */
export const getCurrentUser = async (): Promise<{
	user: User | null;
	error: AuthError | null;
}> => {
	const { data, error } = await supabaseClient.auth.getUser();

	return {
		user: data.user,
		error: adaptAuthError(error),
	};
};
